import { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertCircle, Plus, Trash2, ShieldAlert, Pencil, X, UserSearch } from 'lucide-react';
import {
  checkUserSoD,
  createSodRule,
  deleteSodRule,
  fetchSodRules,
  updateSodRule,
} from './securityAccessMapApi';

function emptyForm() {
  return { ruleName: '', privileges: '', description: '', connectionId: '', riskLevel: 'High' };
}

export default function SodRulesPanel({ embedded = true }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [ruleName, setRuleName] = useState('');
  const [privileges, setPrivileges] = useState('');
  const [description, setDescription] = useState('');
  const [connectionId, setConnectionId] = useState('');
  const [riskLevel, setRiskLevel] = useState('High');
  const [saving, setSaving] = useState(false);
  const [checkUsername, setCheckUsername] = useState('');
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkError, setCheckError] = useState('');
  const [checkResult, setCheckResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await fetchSodRules();
      setRules(list);
    } catch (err) {
      setError(err.message || 'Failed to load SoD rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function resetForm() {
    setEditingId(null);
    const empty = emptyForm();
    setRuleName(empty.ruleName);
    setPrivileges(empty.privileges);
    setDescription(empty.description);
    setConnectionId(empty.connectionId);
    setRiskLevel(empty.riskLevel);
  }

  function startEdit(rule) {
    setEditingId(rule.id);
    setRuleName(rule.ruleName || '');
    setPrivileges((rule.conflictingPrivileges || []).join(', '));
    setDescription(rule.description || '');
    setConnectionId(rule.connectionId || '');
    setRiskLevel(rule.riskLevel === 'Medium' ? 'Medium' : 'High');
    setError('');
  }

  function parsePrivileges() {
    return privileges
      .split(/[\n,]+/)
      .map((p) => p.trim())
      .filter(Boolean);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const name = ruleName.trim();
    const privs = parsePrivileges();
    if (!name || privs.length < 2) {
      setError('Rule name and at least two privilege codes (comma or newline separated) are required.');
      return;
    }
    setSaving(true);
    setError('');
    const payload = {
      ruleName: name,
      conflictingPrivileges: privs,
      description: description.trim(),
      riskLevel,
      connectionId: connectionId.trim() || null,
    };
    try {
      if (editingId) {
        await updateSodRule(editingId, payload);
      } else {
        await createSodRule(payload);
      }
      resetForm();
      await load();
    } catch (err) {
      setError(err.message || (editingId ? 'Failed to update rule' : 'Failed to create rule'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id, e) {
    e?.stopPropagation?.();
    if (!window.confirm('Delete this SoD rule?')) return;
    try {
      await deleteSodRule(id);
      if (editingId === id) resetForm();
      await load();
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  }

  async function handleCheckUser(e) {
    e?.preventDefault();
    const u = checkUsername.trim();
    if (!u) {
      setCheckError('Enter a Fusion username to check.');
      setCheckResult(null);
      return;
    }
    setCheckLoading(true);
    setCheckError('');
    setCheckResult(null);
    try {
      const result = await checkUserSoD({ username: u });
      setCheckResult(result);
    } catch (err) {
      setCheckError(err.message || 'SoD check failed');
      setCheckResult(null);
    } finally {
      setCheckLoading(false);
    }
  }

  const checkViolations =
    checkResult?.violations || checkResult?.conflicts || [];

  const shellClass = 'rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-4';

  return (
    <div className={shellClass}>
      {embedded ? (
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-rose-600" />
          <h3 className="font-semibold text-slate-900">SoD rules</h3>
        </div>
      ) : null}
      <section className="border-b border-slate-100 pb-4 space-y-3">
        <div className="flex items-center gap-2">
          <UserSearch className="h-5 w-5 text-rose-600" />
          <h3 className="font-semibold text-slate-900">SoD violation check</h3>
        </div>
        <form onSubmit={handleCheckUser} className="flex flex-wrap items-end gap-2">
          <label className="flex-1 min-w-[12rem]">
            <span className="sr-only">Fusion username</span>
            <input
              type="text"
              placeholder="Fusion username"
              value={checkUsername}
              onChange={(e) => setCheckUsername(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
              autoComplete="off"
            />
          </label>
          <button
            type="submit"
            disabled={checkLoading}
            className="inline-flex items-center gap-2 rounded-lg bg-rose-700 px-4 py-2 text-sm font-medium text-white hover:bg-rose-600 disabled:opacity-50"
          >
            {checkLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <UserSearch className="h-4 w-4" />
            )}
            Check user
          </button>
        </form>
        {checkError ? (
          <p className="text-sm text-rose-600 flex gap-2">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {checkError}
          </p>
        ) : null}
        {checkLoading ? (
          <p className="text-xs text-slate-500">Checking segregation of duties…</p>
        ) : null}
        {checkResult && !checkLoading ? (
          <div className="space-y-2">
            <p className="text-xs text-slate-600">
              <span className="font-medium text-slate-800">{checkResult.username}</span>
              {' · '}
              {checkResult.rulesEvaluated ?? 0} rule(s) evaluated
            </p>
            {checkViolations.length === 0 ? (
              <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                No SoD violations found for this user.
              </p>
            ) : (
              checkViolations.map((c, i) => (
                <div
                  key={`${c.ruleName}-${i}`}
                  className="rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-900"
                  role="alert"
                >
                  <span className="font-semibold">
                    ⚠️ SoD Conflict{c.riskLevel ? ` (${c.riskLevel})` : ''}:
                  </span>{' '}
                  {c.ruleName ? (
                    <span className="font-medium">{c.ruleName}</span>
                  ) : null}
                  {c.ruleName && (c.description || c.message) ? ' — ' : null}
                  {c.description || c.message}
                  {c.matchedPrivileges?.length ? (
                    <span className="mt-1 block text-xs text-rose-800/90">
                      Matched: {c.matchedPrivileges.join(', ')}
                    </span>
                  ) : null}
                </div>
              ))
            )}
          </div>
        ) : null}
      </section>
      {error ? (
        <p className="text-sm text-rose-600 flex gap-2">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </p>
      ) : null}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading rules…
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 text-sm">
          {rules.length === 0 ? (
            <li className="py-3 text-slate-500">No rules yet (seed runs on first list/check).</li>
          ) : (
            rules.map((r) => {
              const isEditing = editingId === r.id;
              return (
                <li key={r.id} className="py-2">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => startEdit(r)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        startEdit(r);
                      }
                    }}
                    className={`flex items-start gap-2 rounded-lg px-2 py-2 -mx-2 cursor-pointer transition-colors ${
                      isEditing
                        ? 'bg-rose-50 ring-1 ring-rose-200'
                        : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900">{r.ruleName}</div>
                      <div className="text-xs text-slate-600 mt-0.5">
                        {(r.conflictingPrivileges || []).join(' + ')}
                      </div>
                      {r.description ? (
                        <p className="text-xs text-slate-500 mt-1">{r.description}</p>
                      ) : null}
                      <div className="text-[11px] text-slate-400 mt-1 flex flex-wrap gap-2">
                        <span>{r.riskLevel || 'High'}</span>
                        <span>
                          {r.connectionId ? `Connection: ${r.connectionId}` : 'Global (all connections)'}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        startEdit(r);
                      }}
                      className="shrink-0 rounded p-1 text-slate-400 hover:text-cyan-700"
                      title="Edit rule"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDelete(r.id, e)}
                      className="shrink-0 rounded p-1 text-slate-400 hover:text-rose-600"
                      title="Delete rule"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              );
            })
          )}
        </ul>
      )}
      <form onSubmit={handleSubmit} className="border-t border-slate-100 pt-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-medium text-slate-700">
            {editingId ? 'Edit rule' : 'Add rule'}
          </p>
          {editingId ? (
            <button
              type="button"
              onClick={resetForm}
              className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
            >
              <X className="h-3.5 w-3.5" />
              Cancel
            </button>
          ) : null}
        </div>
        <input
          type="text"
          placeholder="Rule name"
          value={ruleName}
          onChange={(e) => setRuleName(e.target.value)}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
        <textarea
          placeholder="Conflicting privileges (comma or newline)"
          value={privileges}
          onChange={(e) => setPrivileges(e.target.value)}
          rows={2}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono text-[12px]"
        />
        <input
          type="text"
          placeholder="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
        <input
          type="text"
          placeholder="Connection ID (optional — leave blank for global rule)"
          value={connectionId}
          onChange={(e) => setConnectionId(e.target.value)}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono text-[12px]"
        />
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <span className="text-xs font-medium">Risk level</span>
          <select
            value={riskLevel}
            onChange={(e) => setRiskLevel(e.target.value)}
            className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="High">High</option>
            <option value="Medium">Medium</option>
          </select>
        </label>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-lg bg-rose-700 px-4 py-2 text-sm font-medium text-white hover:bg-rose-600 disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : editingId ? (
            <Pencil className="h-4 w-4" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          {editingId ? 'Save changes' : 'Add SoD rule'}
        </button>
      </form>
    </div>
  );
}
